import { useRef } from 'react';

/**
 * Stable identity for pieces that live in board cells without ids.
 *
 * The server publishes a board as an array of cells, and a move is two cells
 * changing — nothing in the payload says "this cap went there". React keyed by
 * cell therefore REMOUNTS a moved piece, and a remount cannot slide. This
 * derives the missing identity: a piece that vanished from one cell while an
 * identical-looking piece appeared in another IS that piece, and keeps its key,
 * so React moves the DOM node and a CSS transition carries it across the felt.
 *
 * The matching is deliberately timid. It transfers a key only when exactly one
 * piece of an owner left and exactly one arrived — the shape of every legal
 * move in dama and gera, captures included, because captured pieces leave
 * without arriving anywhere. Anything murkier (a resync replaying five plies,
 * a whole new hand) mints fresh keys for whatever it cannot explain: the worst
 * case is a piece that blinks instead of slides, which is exactly what the
 * boards did before this existed.
 */

export interface SteadyState<T> {
  cells: readonly (T | null)[];
  keys: readonly string[];
  minted: number;
}

/** One frame of the diff. Pure — the hook below is a ref around this. */
export function steadyStep<T>(
  prev: SteadyState<T> | null,
  cells: readonly (T | null)[],
  owner: (piece: T) => number,
): SteadyState<T> {
  if (prev === null || prev.cells.length !== cells.length) {
    let minted = prev?.minted ?? 0;
    return {
      cells,
      keys: cells.map((c) => (c === null ? '' : `p${minted++}`)),
      minted,
    };
  }
  if (prev.cells === cells) return prev;

  const keys = new Array<string>(cells.length).fill('');
  /** Keys that left a cell this frame, bucketed by the owner that held them. */
  const vacated = new Map<number, string[]>();
  const appeared: number[] = [];

  for (let i = 0; i < cells.length; i++) {
    const before = prev.cells[i] ?? null;
    const after = cells[i] ?? null;
    if (after !== null && before !== null && owner(before) === owner(after)) {
      keys[i] = prev.keys[i] ?? '';
    } else {
      if (before !== null) {
        const o = owner(before);
        const bucket = vacated.get(o) ?? [];
        bucket.push(prev.keys[i] ?? '');
        vacated.set(o, bucket);
      }
      if (after !== null) appeared.push(i);
    }
  }

  /* Strictly one-to-one: two arrivals against one departure is not a move
     this diff can attribute, and a guessed slide is worse than a blink. */
  const arrivals = new Map<number, number>();
  for (const i of appeared) {
    const piece = cells[i] ?? null;
    if (piece !== null) arrivals.set(owner(piece), (arrivals.get(owner(piece)) ?? 0) + 1);
  }

  let minted = prev.minted;
  for (const i of appeared) {
    const piece = cells[i] ?? null;
    if (piece === null) continue;
    const o = owner(piece);
    const carried = vacated.get(o)?.[0];
    if (carried !== undefined && carried !== '' && vacated.get(o)?.length === 1 && arrivals.get(o) === 1) {
      keys[i] = carried;
    } else {
      keys[i] = `p${minted++}`;
    }
  }

  return { cells, keys, minted };
}

/** The board-facing shell: one identity stream per mounted board. */
export function useSteadyKeys<T>(
  cells: readonly (T | null)[],
  owner: (piece: T) => number,
): readonly string[] {
  const state = useRef<SteadyState<T> | null>(null);
  state.current = steadyStep(state.current, cells, owner);
  return state.current.keys;
}
